import { useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import type {
  CoachingClip,
  CoachingNote,
  FeedbackBundle,
  MatchSummary,
  PlayerGoal,
  Player,
} from "@/lib/types";
import { FeedbackPlayerModal } from "@/features/playback/FeedbackPlayerModal";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { useMyPlayerDevelopment } from "./hooks/useDevelopmentProfile";
import { matchLabelFor, matchesById } from "./feedbackTabs";
import {
  noteDisplayTitle,
  shortDateLabel,
  summarySections,
} from "./feedbackHelpers";
import {
  FeedbackThumb,
  ObservationThumb,
} from "./components/FeedbackCardBits";
import { BoardChip, boardHasContent } from "./components/BoardChip";
import { FeedbackMediaThumbnail } from "@/features/coach-review/components/FeedbackMediaThumbnail";
import { TacticalBoardThumbnail } from "@/features/canvas/TacticalBoardThumbnail";
import { buildPlayerResponseIndex } from "./responseSignals";

/**
 * Feedback Overview — the action-first landing surface for `/feedback`.
 *
 * PR 1 of the Feedback Player UX redesign. Replaces the legacy
 * Playlists default with a page that answers "what should I do next?"
 * in five compact sections, derived entirely from the existing
 * `/api/my-feedback` bundle + the viewer development endpoint. **No
 * backend changes.**
 *
 * Section order matches the mockup (single column on mobile, two-up
 * on >=md):
 *   1. Continue Review hero — the first unreviewed note,
 *      or the most-recent untracked clip / summary, with a single
 *      strong primary action.
 *   2. Active Goals preview — up to two goals from the development
 *      profile; falls back to "no goals shared yet" copy.
 *   3. New From Coach — up to five unreviewed notes/clips.
 *   4. Latest Match Summary — most-recent match summary, with the
 *      first non-empty recap section as a preview.
 *
 * Privacy: every input is already viewer-filtered server-side. We
 * never template `coach_private_note` here; the only note fields
 * touched are `title`, `event_title`, `note_type`, `player_summary`
 * via `noteDisplayTitle` (which already handles the observation
 * fallback chain).
 *
 * The full Unified Review queue and the redesigned Goals page land in
 * PR 2 / PR 3 — this surface intentionally stays lean.
 */

export type FeedbackOverviewTabProps = {
  bundle: FeedbackBundle | undefined;
  reviewedNoteIds: Set<number>;
};

/** A single normalized item the Overview can recommend or list.
 * The full normalization layer (Phase 2 of the plan) lands in PR 2 —
 * this is the local Overview-only subset. */
type OverviewItem =
  | { kind: "note"; note: CoachingNote; isNew: boolean }
  | { kind: "clip"; clip: CoachingClip }
  | { kind: "summary"; summary: MatchSummary };

export function FeedbackOverviewTab({
  bundle,
  reviewedNoteIds,
}: FeedbackOverviewTabProps) {
  // Single-player guardian flows are the common case; multi-player
  // guardians get a player selector in PR 3 (out of scope here). For
  // now the Active Goals preview reads against the first linked
  // player — same behavior the existing Development tab uses today.
  const activePlayerId = bundle?.players?.[0]?.id ?? null;
  const development = useMyPlayerDevelopment(activePlayerId);

  const matches = useMemo(() => matchesById(bundle), [bundle]);
  // PR 6 — player response signals from the bundle's `reviews[]`.
  // Threaded into the player modal so the chips show "you said X"
  // when reopening an item the player already responded to.
  const responseIndex = useMemo(
    () => buildPlayerResponseIndex(bundle?.reviews ?? []),
    [bundle?.reviews],
  );

  const newNotes = useMemo(
    () => (bundle?.notes ?? []).filter((n) => !reviewedNoteIds.has(n.id)),
    [bundle?.notes, reviewedNoteIds],
  );
  const latestClip = bundle?.clips?.[0];
  const latestSummary = useMemo(() => {
    // Pick the most recent summary by updated_at, falling back to the
    // first entry if there's no timestamp.
    const list = bundle?.match_summaries ?? [];
    if (list.length === 0) return undefined;
    return [...list].sort((a, b) => {
      const at = a.updated_at ? Date.parse(a.updated_at) : 0;
      const bt = b.updated_at ? Date.parse(b.updated_at) : 0;
      return bt - at;
    })[0];
  }, [bundle?.match_summaries]);

  const continueItem: OverviewItem | undefined = useMemo(() => {
    if (newNotes.length > 0) {
      return { kind: "note", note: newNotes[0]!, isNew: true };
    }
    if (latestClip) return { kind: "clip", clip: latestClip };
    if (latestSummary) return { kind: "summary", summary: latestSummary };
    return undefined;
  }, [newNotes, latestClip, latestSummary]);

  // "New from coach" — up to 5 items, interleaved: prefer unreviewed
  // notes, fall back to clips. We don't dedupe across
  // sections — Continue Review intentionally leads with the same
  // first item; the mockup shows it that way ("Pick up where you
  // left off" + a list of all new items).
  const newFromCoach: OverviewItem[] = useMemo(() => {
    const out: OverviewItem[] = [];
    if (out.length < 5) {
      for (const n of newNotes) {
        out.push({ kind: "note", note: n, isNew: true });
        if (out.length >= 5) break;
      }
    }
    if (out.length < 5) {
      for (const c of bundle?.clips ?? []) {
        out.push({ kind: "clip", clip: c });
        if (out.length >= 5) break;
      }
    }
    return out;
  }, [newNotes, bundle?.clips]);

  // Open-in-modal state — notes and clips route through
  // `<FeedbackPlayerModal>`. Summary stays in-page (no modal — the
  // user clicks through to Review > summaries in this PR; PR 2 will
  // inline-expand).
  const [openNote, setOpenNote] = useState<CoachingNote | null>(null);
  const [openClip, setOpenClip] = useState<CoachingClip | null>(null);

  const goals = development.data?.active_goals ?? [];

  // Empty-state guard — no bundle data AND no goals ⇒ one friendly
  // panel, not five empty sections.
  const totallyEmpty =
    !continueItem &&
    newFromCoach.length === 0 &&
    !latestSummary &&
    goals.length === 0;

  return (
    <div className="flex flex-col gap-6" data-testid="feedback-overview">
      {totallyEmpty ? (
        <EmptyOverview />
      ) : (
        <>
          {continueItem ? (
            <ContinueReviewHero
              item={continueItem}
              matches={matches}
              onOpenNote={setOpenNote}
              onOpenClip={setOpenClip}
            />
          ) : null}

          {/* `grid-cols-1` + `md:grid-cols-2` makes the implicit
           * track explicit with `minmax(auto, 1fr)`; that prevents a
           * single un-breakable string (long URLs, slug fixtures) in
           * a child from stretching the column past the container at
           * phone widths — the default `grid` of one auto-track
           * sizes to content, not container. */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ActiveGoalsPreview
              goals={goals}
              isLoading={development.isLoading}
              hasPlayerLink={!!activePlayerId}
            />
            <NewFromCoachList
              items={newFromCoach}
              matches={matches}
              onOpenNote={setOpenNote}
              onOpenClip={setOpenClip}
            />
          </div>

          {latestSummary ? (
            <LatestSummaryPreview
              summary={latestSummary}
              matches={matches}
            />
          ) : null}
        </>
      )}

      {/* Reuse the M5 focused player for in-Overview opens. */}
      {openNote ? (
        <FeedbackPlayerModal
          target={{ kind: "note", note: openNote }}
          match={{ id: openNote.match_id ?? "", team_id: openNote.team_id }}
          onClose={() => setOpenNote(null)}
          players={bundle?.players}
          reviewedNoteIds={reviewedNoteIds}
          playerResponses={responseIndex}
        />
      ) : null}
      {openClip ? (
        <FeedbackPlayerModal
          target={{ kind: "clip", clip: openClip }}
          match={{ id: openClip.match_id ?? "", team_id: openClip.team_id }}
          onClose={() => setOpenClip(null)}
          players={bundle?.players}
          reviewedNoteIds={reviewedNoteIds}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Continue Review hero
// ---------------------------------------------------------------------------

function ContinueReviewHero({
  item,
  matches,
  onOpenNote,
  onOpenClip,
}: {
  item: OverviewItem;
  matches: Map<string, import("@/lib/types").FeedbackMatch>;
  onOpenNote: (n: CoachingNote) => void;
  onOpenClip: (c: CoachingClip) => void;
}) {
  const navigate = useNavigate({ from: "/feedback" });
  const { kicker, title, detail, matchLabel, action, onClick } = useMemo(() => {
    if (item.kind === "note") {
      const n = item.note;
      const isObservation = (n.note_context ?? "video") === "observation";
      return {
        kicker: "New from coach",
        title: noteDisplayTitle(n),
        detail:
          (n.player_summary ?? n.body ?? "").trim() ||
          (isObservation
            ? "A tactical observation for you to review."
            : "A coaching note for you to watch."),
        matchLabel: matchLabelFor(matches, n.match_id),
        action: isObservation ? "Review board" : "Watch note",
        onClick: () => onOpenNote(n),
      };
    }
    if (item.kind === "clip") {
      const c = item.clip;
      return {
        kicker: "New from coach",
        title: c.title?.trim() || "Match clip",
        detail:
          c.description?.trim() ||
          "A clip your coach selected from a recent match.",
        matchLabel: matchLabelFor(matches, c.match_id),
        action: "Watch clip",
        onClick: () => onOpenClip(c),
      };
    }
    // summary
    const s = item.summary;
    const firstSection = summarySections(s)[0];
    return {
      kicker: "Latest from coach",
      title: matchLabelFor(matches, s.match_id) || "Match summary",
      detail:
        firstSection?.[1]?.trim() ||
        "Your coach shared a recap of the match.",
      matchLabel: undefined,
      action: "Read summary",
      onClick: () => {
        // Summary doesn't open in the player modal — deep-link to
        // the Review tab with this summary's id so the tab lands
        // on the Summaries filter AND inline-expands this card,
        // not just the section. PR 2 router schema added the
        // `summary` param. SPA navigation via TanStack Router
        // preserves the TanStack Query cache so the next render
        // doesn't flash a loading state.
        navigate({
          to: "/feedback",
          search: { tab: "review", summary: item.summary.id },
        });
      },
    };
  }, [item, matches, navigate, onOpenNote, onOpenClip]);

  return (
    <article
      data-testid="feedback-overview-hero"
      data-overview-item-kind={item.kind}
      className="rounded border border-rule bg-surface p-4 shadow-sm sm:p-5"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        {/* Hero thumb — larger than the per-card preview so the
         * primary action gets the strongest visual weight. Mockup
         * shows it on the left at desktop, stacked above on mobile. */}
        <HeroThumb item={item} />
        <div className="min-w-0 flex-1">
          <p className="text-[0.6rem] font-display uppercase tracking-wider text-accent">
            {kicker}
          </p>
          <h2
            className="font-display text-xl sm:text-2xl tracking-tight text-ink mt-1"
            data-testid="feedback-overview-hero-title"
          >
            {title}
          </h2>
          {matchLabel ? (
            <p className="text-ink-muted text-xs font-mono mt-1">{matchLabel}</p>
          ) : null}
          <p className="text-sm text-ink-muted mt-3 line-clamp-3">{detail}</p>
          <div className="mt-4">
            <Button
              variant="primary"
              onClick={onClick}
              data-testid="feedback-overview-hero-action"
            >
              {action}
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Per-kind thumb choosers (Overview's mini + hero variants)
// ---------------------------------------------------------------------------

/** Small ~80×48 preview rendered to the left of "New from coach"
 * row labels. Same chooser logic as the unified Review card. */
function OverviewRowThumb({ item }: { item: OverviewItem }) {
  if (item.kind === "note") {
    const note = item.note;
    const isObservation = (note.note_context ?? "video") === "observation";
    if (isObservation) {
      return boardHasContent(note.tactical_board_json) ? (
        <BoardChip raw={note.tactical_board_json} />
      ) : (
        <ObservationThumb />
      );
    }
    return (
      <FeedbackThumb
        kind="note"
        id={note.id}
        hasThumbnail={note.has_thumbnail ?? true}
      />
    );
  }
  if (item.kind === "clip") {
    return <FeedbackThumb kind="clip" id={item.clip.id} />;
  }
  // summary — New From Coach excludes summaries today, so this is
  // defensive only; keep the row layout intact.
  return null;
}

/** Bigger hero variant — same preview content, ~240×135 on >=sm
 * so it carries weight against the title + action button. Stacked
 * above on mobile to keep the action visible without scrolling. */
function HeroThumb({ item }: { item: OverviewItem }) {
  // Summary heroes don't have a media preview — keep the layout
  // clean by skipping the slot rather than rendering a placeholder.
  if (item.kind === "summary") return null;

  // Common envelope styles (16:9 box, max width caps so the title
  // column keeps reasonable breathing room on wide viewports).
  const envelope =
    "w-full sm:w-60 md:w-72 shrink-0 overflow-hidden rounded border border-rule";

  if (item.kind === "note") {
    const note = item.note;
    const isObservation = (note.note_context ?? "video") === "observation";
    if (isObservation) {
      // Observation heroes use the shared board thumbnail at the
      // larger hero footprint.
      if (boardHasContent(note.tactical_board_json)) {
        return (
          <TacticalBoardThumbnail
            raw={note.tactical_board_json}
            width={288}
            fallback="none"
            testId="feedback-overview-hero-thumb"
            className={`${envelope} flex aspect-video items-center justify-center bg-surface`}
          />
        );
      }
      return (
        <div className={envelope} data-testid="feedback-overview-hero-thumb">
          <div className="flex aspect-video items-center justify-center bg-surface">
            <ObservationThumb />
          </div>
        </div>
      );
    }
    return <HeroJpegThumb kind="note" id={note.id} envelope={envelope} />;
  }
  if (item.kind === "clip") {
    return (
      <HeroJpegThumb kind="clip" id={item.clip.id} envelope={envelope} />
    );
  }
  return null;
}

/** Hero variant of `FeedbackThumb` — same auth-fetched JPEG, but
 * filling the hero envelope instead of the small per-card tile. */
function HeroJpegThumb({
  kind,
  id,
  envelope,
}: {
  kind: "note" | "clip";
  id: number;
  envelope: string;
}) {
  return (
    <FeedbackMediaThumbnail
      kind={kind}
      id={id}
      testId="feedback-overview-hero-thumb"
      placeholderTestId="feedback-overview-hero-thumb-placeholder"
      className={`${envelope} block aspect-video bg-gradient-to-br from-canvas to-surface`}
      imgClassName="block aspect-video w-full object-cover"
    />
  );
}

// ---------------------------------------------------------------------------
// Active Goals preview
// ---------------------------------------------------------------------------

function ActiveGoalsPreview({
  goals,
  isLoading,
  hasPlayerLink,
}: {
  goals: PlayerGoal[];
  isLoading: boolean;
  hasPlayerLink: boolean;
}) {
  return (
    <section
      className="rounded border border-rule bg-surface p-4 shadow-sm"
      aria-labelledby="overview-goals-heading"
      data-testid="feedback-overview-goals"
    >
      <header className="flex items-baseline justify-between gap-2">
        <h3
          id="overview-goals-heading"
          className="font-display text-base text-ink"
        >
          Active goals
        </h3>
        <Link
          to="/feedback"
          search={{ tab: "goals" }}
          className="text-xs font-display uppercase tracking-wider text-ink-muted hover:text-accent"
        >
          My plan →
        </Link>
      </header>
      <div className="mt-3">
        {!hasPlayerLink ? (
          <p className="text-sm text-ink-muted">
            No linked player on this account yet — ask your coach to add one.
          </p>
        ) : isLoading ? (
          <p
            className="text-sm font-mono text-ink-muted"
            data-testid="feedback-overview-goals-loading"
          >
            Loading goals…
          </p>
        ) : goals.length === 0 ? (
          <p className="text-sm text-ink-muted">
            No goals shared yet. Your coach can add a goal from your roster
            profile or from a note.
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {goals.slice(0, 2).map((g) => (
              <GoalPreviewRow key={g.id} goal={g} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function GoalPreviewRow({ goal }: { goal: PlayerGoal }) {
  return (
    <li
      data-testid="feedback-overview-goal"
      className="rounded border border-rule bg-surface p-3"
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className="font-display text-sm text-ink truncate">{goal.title}</p>
        {goal.target_date ? (
          <span className="text-[0.65rem] font-mono uppercase tracking-wider text-ink-muted shrink-0">
            by {shortDateLabel(goal.target_date)}
          </span>
        ) : null}
      </div>
      {goal.success_criteria ? (
        <p className="text-xs text-ink-muted mt-1 line-clamp-2">
          {goal.success_criteria}
        </p>
      ) : null}
    </li>
  );
}

// ---------------------------------------------------------------------------
// New From Coach list
// ---------------------------------------------------------------------------

function NewFromCoachList({
  items,
  matches,
  onOpenNote,
  onOpenClip,
}: {
  items: OverviewItem[];
  matches: Map<string, import("@/lib/types").FeedbackMatch>;
  onOpenNote: (n: CoachingNote) => void;
  onOpenClip: (c: CoachingClip) => void;
}) {
  return (
    <section
      className="rounded border border-rule bg-surface p-4 shadow-sm"
      aria-labelledby="overview-new-heading"
      data-testid="feedback-overview-new"
    >
      <header className="flex items-baseline justify-between gap-2">
        <h3
          id="overview-new-heading"
          className="font-display text-base text-ink"
        >
          New from coach
        </h3>
        <Link
          to="/feedback"
          search={{ tab: "review" }}
          className="text-xs font-display uppercase tracking-wider text-ink-muted hover:text-accent"
        >
          See all →
        </Link>
      </header>
      <div className="mt-3">
        {items.length === 0 ? (
          <p className="text-sm text-ink-muted">
            Nothing new since you last checked.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {items.map((item, idx) => (
              <NewFromCoachRow
                key={rowKey(item, idx)}
                item={item}
                matches={matches}
                onOpenNote={onOpenNote}
                onOpenClip={onOpenClip}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function rowKey(item: OverviewItem, idx: number) {
  if (item.kind === "note") return `nt-${item.note.id}`;
  if (item.kind === "clip") return `cl-${item.clip.id}`;
  return `idx-${idx}`;
}

function NewFromCoachRow({
  item,
  matches,
  onOpenNote,
  onOpenClip,
}: {
  item: OverviewItem;
  matches: Map<string, import("@/lib/types").FeedbackMatch>;
  onOpenNote: (n: CoachingNote) => void;
  onOpenClip: (c: CoachingClip) => void;
}) {
  let title = "";
  let meta = "";
  let badge = "";
  let onClick: () => void = () => {};

  if (item.kind === "note") {
    const n = item.note;
    title = noteDisplayTitle(n);
    meta = matchLabelFor(matches, n.match_id) ?? "Note";
    badge =
      (n.note_context ?? "video") === "observation" ? "Observation" : "Note";
    onClick = () => onOpenNote(n);
  } else if (item.kind === "clip") {
    const c = item.clip;
    title = c.title?.trim() || "Match clip";
    meta = matchLabelFor(matches, c.match_id) ?? "Clip";
    badge = "Clip";
    onClick = () => onOpenClip(c);
  }

  return (
    <li>
      <button
        type="button"
        data-testid="feedback-overview-new-row"
        data-overview-item-kind={item.kind}
        onClick={onClick}
        className="flex w-full items-center gap-3 rounded border border-rule bg-canvas px-3 py-2 text-left transition-colors hover:bg-field focus:outline-none focus:ring-2 focus:ring-accent"
      >
        {/* Per-kind preview tile — same chooser the unified Review
         * card uses, so the Overview "New from coach" rows feel
         * like the same surface in miniature. */}
        <OverviewRowThumb item={item} />
        <Badge variant="outline" className="shrink-0">
          {badge}
        </Badge>
        <span className="min-w-0 flex-1">
          <span className="block font-display text-sm text-ink truncate">
            {title}
          </span>
          <span className="block text-xs font-mono text-ink-muted truncate">
            {meta}
          </span>
        </span>
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Latest Match Summary preview
// ---------------------------------------------------------------------------

function LatestSummaryPreview({
  summary,
  matches,
}: {
  summary: MatchSummary;
  matches: Map<string, import("@/lib/types").FeedbackMatch>;
}) {
  const matchLabel = matchLabelFor(matches, summary.match_id);
  const firstSection = summarySections(summary)[0];

  return (
    <section
      className="rounded border border-rule bg-surface p-4 shadow-sm"
      aria-labelledby="overview-summary-heading"
      data-testid="feedback-overview-summary"
    >
      <header className="flex items-baseline justify-between gap-2">
        <h3
          id="overview-summary-heading"
          className="font-display text-base text-ink"
        >
          Latest match recap
        </h3>
        <Link
          to="/feedback"
          /* Deep-link to this specific recap (PR 2 router schema).
           * The Review tab auto-selects the Summaries filter and
           * inline-expands this summary card on mount. */
          search={{ tab: "review", summary: summary.id }}
          className="text-xs font-display uppercase tracking-wider text-ink-muted hover:text-accent"
        >
          Read full recap →
        </Link>
      </header>
      {matchLabel ? (
        <p className="text-xs font-mono text-ink-muted mt-1">{matchLabel}</p>
      ) : null}
      {firstSection ? (
        <p className="text-sm text-ink mt-3">
          <span className="font-display text-ink">{firstSection[0]} — </span>
          <span className="text-ink-muted">{firstSection[1]}</span>
        </p>
      ) : (
        <p className="text-sm text-ink-muted mt-3">
          Your coach shared a recap of the match.
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyOverview() {
  return (
    <section
      className="rounded border border-rule bg-surface p-6 text-center shadow-sm"
      data-testid="feedback-overview-empty"
    >
      <p className="font-display text-lg text-ink">
        No feedback has been shared yet.
      </p>
      <p className="text-sm text-ink-muted mt-2 max-w-md mx-auto">
        When your coach shares notes, clips, match recaps, or goals, they will
        appear here.
      </p>
    </section>
  );
}

// Re-export so a future caller (PR 2 unified review, PR 3 goals) can
// reach for the shared helpers without re-importing from feedbackTabs.
export { matchLabelFor };

// Suppress an unused-import warning when the file is consumed by a
// component that doesn't reach for Player.
export type { Player };
